import {mergeMap} from 'rxjs/operators';

import {FrontWindow, globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskMetadata} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {fromIdleOrHiddenCallback} from '../../../srcCommon/rx/fromIdleOrHiddenCallback';
import {CancelToken, toCancelablePromise} from '../asyncCancelHelpers';

/**
 * Await-able version of requestIdleCallback that propagates the return value.
 *
 * Warning: idle callbacks don't fire while the window is hidden or obscured. Make sure you target the right window!
 */
export async function inIdleCallbackAsync<R>(
  window: FrontWindow,
  callback: (deadline: IdleDeadline) => R | Promise<R>,
  metadata: TaskMetadata,
  timeout?: number,
  cancelToken?: CancelToken,
): Promise<R> {
  return new Promise<R>((resolve, reject) => {
    const handle = window.requestInstrumentedIdleCallback(
      (deadline) => {
        try {
          resolve(callback(deadline));
        } catch (error) {
          reject(error);
        }
      },
      metadata,
      timeout ? {timeout} : undefined,
    );

    if (!cancelToken) {
      return;
    }

    cancelToken.promise.then(() => {
      window.cancelIdleCallback(handle);
    });
  });
}

export async function inIdleOrHiddenCallbackAsync<R>(
  callback: (deadline: IdleDeadline) => R | Promise<R>,
  metadata: TaskMetadata,
  timeout?: number,
  cancelToken?: CancelToken,
  window = globalWindow,
): Promise<R> {
  return toCancelablePromise(
    fromIdleOrHiddenCallback(metadata, timeout, window).pipe(
      mergeMap(async (deadline) => callback(deadline)),
    ),
    cancelToken,
  );
}

export interface IdleIterationSummary {
  idleCycleCount: number;
  blockingDurations: ReadonlyArray<number>;
}

export async function iterateInIdleOrHiddenCallbackAsync<TState, TFinished extends TState>(
  isFinished: (result: TState) => result is TFinished,
  callback: (previousResult: TState) => TState,
  initialResult: TState,
  metadata: TaskMetadata,
  onIterationSummary?: (summary: IdleIterationSummary) => void,
): Promise<TFinished> {
  let result: TState = initialResult;
  const blockingDurations: Array<number> = [];

  const runUntilDeadline = (deadline: IdleDeadline) => {
    const initialTimeRemaining = deadline.timeRemaining();
    const startTime = performance.now();
    while (!isFinished(result) && deadline.timeRemaining()) {
      result = callback(result);
    }

    const duration = performance.now() - startTime;
    blockingDurations.push(Math.max(0, duration - initialTimeRemaining));
  };

  while (!isFinished(result)) {
    // eslint-disable-next-line no-await-in-loop
    await inIdleOrHiddenCallbackAsync(runUntilDeadline, metadata);
  }

  if (onIterationSummary) {
    onIterationSummary({
      idleCycleCount: blockingDurations.length,
      blockingDurations,
    });
  }

  return result;
}

export async function reduceInIdleOrHiddenCallbackAsync<T, U>(
  values: ReadonlyArray<T>,
  callback: (previousResult: U, value: T) => U,
  initialResult: U,
  metadata: TaskMetadata,
): Promise<U> {
  let index = 0;
  let result: U = initialResult;

  const reduceUntilDeadline = (deadline: IdleDeadline) => {
    while (index < values.length && deadline.timeRemaining()) {
      result = callback(result, values[index++]);
    }
  };

  while (index < values.length) {
    // eslint-disable-next-line no-await-in-loop
    await inIdleOrHiddenCallbackAsync(reduceUntilDeadline, metadata);
  }

  return result;
}
